//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import angular, { IScope, IWindowService } from 'angular';
import _ from 'underscore';
import universalImageActionsTributaryCustomizeLinksTemplate from '@legacy-components/common/universal-image-actions/universal-image-actions-tributary-customize-links.html';

export class UniversalImageActionsTributaryCustomizeLinks {
  public state;
  public universalItemId: string;
  public trackingCategory: string;
  public trackingAction: string;
  public trackingLabels;
  public isDesktopExperienceEnabled: boolean;
  public shortenChecked: boolean;
  public shortenCheckedInitialized: boolean;
  public creatingLinkAsync: boolean;
  public shortenLinkError: boolean;
  public hasCopiedLink: boolean;
  public showroomLaunchContext;
  public isContextualShowroom: boolean;

  constructor(
    private $scope: IScope,
    private $window: IWindowService,
    private SessionVarsService,
    private ShowroomApiService,
    private ShowroomService,
    private SHOWROOM_ACTION_TYPES,
    private SHOWROOM_BUTTON_STATE,
    private SHOWROOM_PREVIEW_SECTIONS,
    private TrackingService,
    private UniversalImageActionsService,
  ) {}

  public close = () => {
    this.$scope.$emit('uia:modal:close');
  };

  public addAnotherLink = () => {
    this.ShowroomService.addNewInstance(
      this.universalItemId,
      this.state.impl.id,
      this.state.impl.name,
    );
  };

  public onPassthroughChanged = (showDesktopExperience, passthroughUrl) => {
    this.ShowroomService.updatePreview(
      this.universalItemId,
      this.state.impl.id,
      this.state.impl.name,
      this.state.impl.implementationType,
      this.state.impl.sourceId,
      this.state?.filters?.filterState,
      showDesktopExperience,
      passthroughUrl,
      this.state?.emailCaptureState,
      this.state?.customStringsState,
      this.state?.curatedRecsId,
      this.state?.useCuratedRecommendations,
      this.state?.curatedProducts,
      this.state?.selectedLabels,
      this.state?.productContentState,
      this.state?.mediaContentState,
      this.SHOWROOM_PREVIEW_SECTIONS.NONE,
      () => {
        this.ShowroomService.updatePassthrough(
          showDesktopExperience,
          passthroughUrl,
        );
      },
    );
  };

  public shortenLinkChanged = function () {
    if (!this.shortenChecked) {
      this.TrackingService.recordEvent(
        this.trackingCategory,
        this.trackingAction,
        this.trackingLabels.toggleShortLink(this.shortenChecked),
      );
    }

    // TODO DEV-28858 DEL POST MULTI_LINKS LAUNCH
    _(this.state.instances).each((url: any) => {
      if (this.shortenChecked) {
        if (!_.isUndefined(url.shortLink)) {
          url.urlToShow = url.shortLink;
          url.hasCopiedLink = false;
        } else {
          url.shortLinkOutstandingAsync = 'getTribShortLink';
          this.ShowroomApiService.getTributaryImplShortLinkAsync(
            this.state.impl.id,
            this.state.impl.name,
            this.state.impl.sourceId,
            'item', // From the UIA, only item sourceTypes are supported
          )
            .then((result) => {
              // TODO: Make the shortlink required and throw error if there is none
              if (result.shortLink) {
                url.shortLink = result.shortLink;
                url.urlToShow = url.shortLink;
              } else {
                // Creating a short link failed, disable the checkbox and show a message
                this.shortenLinkError = true;
              }
            })
            .finally(() => {
              url.shortLinkOutstandingAsync = null;
              this.hasCopiedLink = false;
            });
        }
      } else {
        url.urlToShow = url.longLink;
        url.hasCopiedLink = false;
      }
    });
  };

  public copyToClipboardClicked = () => {
    this.TrackingService.recordEvent(
      this.trackingCategory,
      this.trackingAction,
      this.trackingLabels.copyLink,
    );
  };

  public $onInit() {
    this.UniversalImageActionsService.trackMediaEvent(
      MediaTrackingEventName.ShowroomCreated,
    );
    const launchOptions =
      this.UniversalImageActionsService.getCurrentLaunchOptions();
    this.universalItemId = launchOptions.getUniversalItemId();
    this.showroomLaunchContext = launchOptions.getShowroomLaunchContext() || {};
    this.isContextualShowroom = !_.isEmpty(this.showroomLaunchContext);

    this.trackingCategory = this.ShowroomService.getTrackingCategory();
    this.trackingAction = this.ShowroomService.getTrackingAction();
    this.trackingLabels = {
      addLink: 'AddAnotherLinkClicked',
      copyLink: 'Copy',
      toggleShortLink: (selected) =>
        selected ? 'ShortenLinkSelected' : 'ShortenLinkUnselected',
      helpLinkClicked: (linkName) => `HelpLinkClicked=${linkName}`,
    };

    this.state = {
      // Since this is step 4, we have already loaded
      loaded: true,
    };

    this.shortenChecked = true;
    this.creatingLinkAsync = false;
    this.shortenLinkError = false;
    this.isDesktopExperienceEnabled = this.SessionVarsService.isFeatureEnabled(
      'showroom_desktop_experience',
    );

    this.$scope.$on('copy-button:copied', (e) => {
      this.hasCopiedLink = true;
      e.stopPropagation();
    });

    this.ShowroomService.getCurrentConfigStatusObservable().subscribe(
      (currentState) => {
        const oldLoadedState = this.state.loaded;
        this.state = angular.copy(currentState);

        // If this is the first load
        if (!oldLoadedState && this.state.loaded) {
          this.shortenLinkChanged();
        }

        this.creatingLinkAsync = _(this.state.outstandingAsyncActions).contains(
          this.SHOWROOM_ACTION_TYPES.ADD_NEW_INSTANCE,
        );
      },
    );

    // Load the state and set hasBeenUpdated = true since we're now on the final panel
    this.ShowroomService.loadExistingState(this.universalItemId);
    this.ShowroomService.updateHasBeenUpdated();
  }
}

angular
  .module('uia')
  .component('universalImageActionsTributaryCustomizeLinks', {
    template: universalImageActionsTributaryCustomizeLinksTemplate,
    controller: [
      '$scope',
      '$window',
      'SessionVarsService',
      'ShowroomApiService',
      'ShowroomService',
      'SHOWROOM_ACTION_TYPES',
      'SHOWROOM_BUTTON_STATE',
      'SHOWROOM_PREVIEW_SECTIONS',
      'TrackingService',
      'UniversalImageActionsService',
      UniversalImageActionsTributaryCustomizeLinks,
    ],
  });
